import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Users, Building2, Wallet, Eye, TrendingUp, CheckCircle2, Clock, CreditCard, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useCurrency } from "../../context/CurrencyContext";

/**
 * AdminDashboard — shows real DB-backed stats. Every card is clickable and
 * opens a modal with the corresponding filtered list (users, properties,
 * bookings, visits) directly on the same page.
 */
export default function AdminDashboard() {
  const { t } = useLang();
  const { format: money } = useCurrency();
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null); // { title, kind, params }

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const open = (kind, title, params = {}) => setModal({ kind, title, params });
  const close = () => setModal(null);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold">{t("admin_stats")}</h1>
        <p className="text-sm text-[#5C6053] mt-1">{t("admin_only_notice")}</p>
      </div>

      {stats?.activation_requests?.pending > 0 && (
        <Link
          to="/admin/activations"
          data-testid="pending-activations-banner"
          className="block bg-[#FFF3D6] border border-[#E5B25D]/60 rounded-2xl p-4 hover:bg-[#FFECBF] transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E5B25D]/30 text-[#8A6D1F] flex items-center justify-center flex-shrink-0">
              <CreditCard size={18} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-[#8A6D1F]">
                {stats.activation_requests.pending} {t("activation_requests")}
              </div>
              <div className="text-xs text-[#8A6D1F]/80">{t("awaiting_admin_approval")}</div>
            </div>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card testid="admin-total-users" icon={Users} label={t("total_users")} value={stats?.users.total} tone="brand"
          onClick={() => open("users", t("total_users"), { filter_by: "all" })} />
        <Card testid="admin-paid-users" icon={Wallet} label={t("paid_users")} value={stats?.users.paid} tone="green"
          onClick={() => open("users", t("paid_users"), { filter_by: "paid" })} />
        <Card testid="admin-active-trials" icon={Clock} label={t("active_trials")} value={stats?.users.active_trials} tone="accent"
          onClick={() => open("users", t("active_trials"), { filter_by: "trial_active" })} />
        <Card testid="admin-expired-trials" icon={Clock} label={t("expired_trials")} value={stats?.users.expired_trials} tone="red"
          onClick={() => open("users", t("expired_trials"), { filter_by: "trial_expired" })} />
        <Card testid="admin-total-properties" icon={Building2} label={t("total_properties")} value={stats?.properties.total} tone="brand"
          onClick={() => open("properties", t("properties_list"))} />
        <Card testid="admin-pending-activations" icon={CreditCard} label={t("activation_requests")} value={stats?.activation_requests?.pending} tone="accent"
          onClick={() => open("activations", t("activation_requests"))} />
        <Card testid="admin-total-bookings" icon={CheckCircle2} label={t("total_bookings")} value={stats?.bookings.total} tone="green"
          onClick={() => open("bookings", t("bookings_list"))} />
        <Card testid="admin-platform-income" icon={TrendingUp} label={t("platform_income")} value={stats ? money(stats.bookings.platform_income) : "—"} tone="green"
          onClick={() => open("bookings", t("platform_income"), { status: "rented" })} />
      </div>

      <div
        data-testid="visits-chart-card"
        onClick={() => open("visits", t("visits_log"))}
        className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm cursor-pointer hover:border-[#2A5948]/60 transition-colors"
      >
        <div className="flex items-center gap-2 mb-3">
          <Eye size={16} className="text-[#2A5948]" />
          <h3 className="font-semibold">{t("visits")} — {stats?.visits.total ?? 0}</h3>
        </div>
        <div className="w-full h-64">
          <ResponsiveContainer>
            <BarChart data={stats?.visits.by_day || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D8" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#2A5948" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {modal && <DetailModal modal={modal} onClose={close} t={t} />}
    </div>
  );
}

function Card({ icon: Icon, label, value, tone, testid, onClick }) {
  const map = {
    green: "bg-[#E6F1E7] text-[#2A5948]",
    red: "bg-[#FBE9E4] text-[#A85540]",
    brand: "bg-[#2A5948] text-white",
    accent: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-start bg-white border border-[#E5E1D8] rounded-2xl p-4 shadow-sm hover:border-[#2A5948]/60 hover:shadow-md transition-all cursor-pointer"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${map[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="mt-3 text-xs text-[#5C6053]">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value ?? "—"}</div>
    </button>
  );
}

function DetailModal({ modal, onClose, t }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        let url, params = modal.params || {};
        if (modal.kind === "users") url = "/admin/users";
        else if (modal.kind === "properties") url = "/admin/properties";
        else if (modal.kind === "bookings") url = "/admin/bookings";
        else if (modal.kind === "visits") url = "/admin/visits";
        else if (modal.kind === "activations") { url = "/admin/activation-requests"; params = { status: "pending" }; }
        const { data } = await api.get(url, { params });
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiError(e));
          setRows([]);
        }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [modal]);

  return (
    <div
      data-testid="admin-detail-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-[#E5E1D8] shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1D8]">
          <h3 className="text-lg font-semibold">{modal.title}</h3>
          <button data-testid="detail-modal-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F3EFE7]">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {rows === null && <div className="p-8 text-center text-[#5C6053] text-sm">...</div>}
          {rows && rows.length === 0 && <div className="p-8 text-center text-[#5C6053] text-sm">—</div>}
          {rows && rows.length > 0 && <RowsTable kind={modal.kind} rows={rows} t={t} />}
        </div>
      </div>
    </div>
  );
}

function RowsTable({ kind, rows, t }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { format: money } = useCurrency();
  if (kind === "users") {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase sticky top-0">
          <tr>
            <th className="text-start px-4 py-3">{t("name")}</th>
            <th className="text-start px-4 py-3">{t("phone")}</th>
            <th className="text-start px-4 py-3">{t("email")}</th>
            <th className="text-start px-4 py-3">{t("property_count")}</th>
            <th className="text-start px-4 py-3">{t("booking_count")}</th>
            <th className="text-start px-4 py-3">{t("paid_users")}</th>
            <th className="text-start px-4 py-3">{t("joined_at")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} data-testid={`detail-user-${u.id}`} className="border-t border-[#E5E1D8]">
              <td className="px-4 py-3">{u.name}</td>
              <td className="px-4 py-3" dir="ltr">{u.phone}</td>
              <td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3">{u.property_count}</td>
              <td className="px-4 py-3">{u.booking_count}</td>
              <td className="px-4 py-3">{u.is_paid ? <span className="text-xs px-2 py-0.5 rounded bg-[#E6F1E7] text-[#2A5948]">{t("approved")}</span> : "—"}</td>
              <td className="px-4 py-3 text-xs text-[#5C6053]">{u.created_at?.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kind === "properties") {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase sticky top-0">
          <tr>
            <th className="text-start px-4 py-3">{t("property_name")}</th>
            <th className="text-start px-4 py-3">{t("owner")}</th>
            <th className="text-start px-4 py-3">{t("phone")}</th>
            <th className="text-start px-4 py-3">{t("booking_count")}</th>
            <th className="text-start px-4 py-3">{t("filter_approved")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} data-testid={`detail-property-${p.id}`} className="border-t border-[#E5E1D8]">
              <td className="px-4 py-3">{p.name}</td>
              <td className="px-4 py-3">{p.owner_name || "—"}</td>
              <td className="px-4 py-3" dir="ltr">{p.owner_phone || "—"}</td>
              <td className="px-4 py-3">{p.booking_count}</td>
              <td className="px-4 py-3">{p.is_approved ? <span className="text-xs px-2 py-0.5 rounded bg-[#E6F1E7] text-[#2A5948]">{t("approved")}</span> : <span className="text-xs px-2 py-0.5 rounded bg-[#FFF3D6] text-[#8A6D1F]">{t("filter_pending")}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kind === "bookings") {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase sticky top-0">
          <tr>
            <th className="text-start px-4 py-3">{t("booking_date")}</th>
            <th className="text-start px-4 py-3">{t("property_name")}</th>
            <th className="text-start px-4 py-3">{t("owner")}</th>
            <th className="text-start px-4 py-3">{t("booking_status")}</th>
            <th className="text-start px-4 py-3">{t("booking_amount")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} data-testid={`detail-booking-${b.id}`} className="border-t border-[#E5E1D8]">
              <td className="px-4 py-3">{b.date || "—"}</td>
              <td className="px-4 py-3">{b.property_name || "—"}</td>
              <td className="px-4 py-3">{b.owner_name || "—"}</td>
              <td className="px-4 py-3">{b.status}</td>
              <td className="px-4 py-3">{money(b.amount || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kind === "visits") {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase sticky top-0">
          <tr>
            <th className="text-start px-4 py-3">{t("visit_time")}</th>
            <th className="text-start px-4 py-3">{t("ip_address")}</th>
            <th className="text-start px-4 py-3">{t("user_agent")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.id} data-testid={`detail-visit-${v.id}`} className="border-t border-[#E5E1D8]">
              <td className="px-4 py-3 text-xs text-[#5C6053]">{v.ts?.slice(0, 19).replace("T", " ")}</td>
              <td className="px-4 py-3" dir="ltr">{v.ip || "—"}</td>
              <td className="px-4 py-3 text-xs text-[#5C6053] truncate max-w-[420px]">{v.ua || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kind === "activations") {
    return (
      <table className="w-full text-sm">
        <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase sticky top-0">
          <tr>
            <th className="text-start px-4 py-3">{t("name")}</th>
            <th className="text-start px-4 py-3">{t("phone")}</th>
            <th className="text-start px-4 py-3">{t("booking_amount")}</th>
            <th className="text-start px-4 py-3">{t("booking_date")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`detail-activation-${r.id}`} className="border-t border-[#E5E1D8]">
              <td className="px-4 py-3">{r.owner_name || "—"}</td>
              <td className="px-4 py-3" dir="ltr">{r.owner_phone || "—"}</td>
              <td className="px-4 py-3">{r.amount} {(r.currency || "omr").toUpperCase()}</td>
              <td className="px-4 py-3 text-xs text-[#5C6053]">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return null;
}
